import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { DxButtonModule } from 'devextreme-angular';
import { saveAs } from 'file-saver';

/** Characters no file system accepts, plus the ones that read badly in a download bar. */
const UNSAFE_FILE_NAME_CHARACTERS = /[\\/:*?"<>|\u0000-\u001f]+/g;

/** How long a print frame is kept once printing has started; the dialog holds its own copy. */
const PRINT_FRAME_LIFETIME_MS = 60_000;

export const toPdfFileName = (name: string): string => {
  const safeName = name.replace(UNSAFE_FILE_NAME_CHARACTERS, ' ').replace(/\s+/g, ' ').trim() || 'report';
  return safeName.toLowerCase().endsWith('.pdf') ? safeName : `${safeName}.pdf`;
};

/**
 * Download and Print for a rendered report PDF, as buttons of our own.
 *
 * The previews show the PDF in the browser's built-in viewer, whose controls sit in a toolbar
 * we can't style, differ from one browser to the next, and were covered by the floating
 * settings panel. Users didn't find them. These buttons live in each view's header instead,
 * so the two actions people look for are always in the same place.
 */
@Component({
  selector: 'ag-shr-report-pdf-actions',
  standalone: true,
  imports: [DxButtonModule],
  templateUrl: './report-pdf-actions.component.html',
  styleUrls: ['./report-pdf-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportPdfActionsComponent implements OnDestroy {
  @Input() pdfBlob: Blob | null = null;
  /** Download name, with or without `.pdf`. */
  @Input() fileName = 'report';
  @Input() disabled = false;
  @Input() height = 34;

  private printFrame: HTMLIFrameElement | null = null;
  private printUrl: string | null = null;
  private printCleanupTimer: ReturnType<typeof setTimeout> | null = null;

  protected get isUnavailable(): boolean {
    return this.disabled || !this.pdfBlob;
  }

  protected readonly download = (): void => {
    if (this.pdfBlob) {
      saveAs(this.pdfBlob, toPdfFileName(this.fileName));
    }
  };

  /**
   * Prints the PDF itself, not the page around it: a hidden frame loads the same document and
   * asks the browser to print that frame. Where a browser won't print a PDF from a frame, the
   * document opens in a new tab, whose own viewer can print it.
   */
  protected readonly print = (): void => {
    if (!this.pdfBlob) {
      return;
    }

    this.removePrintFrame();
    this.printUrl = URL.createObjectURL(this.pdfBlob);

    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    frame.onload = () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        window.open(this.printUrl ?? '', '_blank', 'noopener');
      }
      this.printCleanupTimer = setTimeout(() => this.removePrintFrame(), PRINT_FRAME_LIFETIME_MS);
    };
    frame.src = this.printUrl;
    document.body.appendChild(frame);
    this.printFrame = frame;
  };

  ngOnDestroy(): void {
    this.removePrintFrame();
  }

  private removePrintFrame(): void {
    if (this.printCleanupTimer) {
      clearTimeout(this.printCleanupTimer);
      this.printCleanupTimer = null;
    }
    this.printFrame?.remove();
    this.printFrame = null;
    if (this.printUrl) {
      URL.revokeObjectURL(this.printUrl);
      this.printUrl = null;
    }
  }
}
